import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { BackNavigationService } from 'app/helpers/services/backButtonNavigationService';
import { UiRepository } from 'app/state/ui.repository';
import { UiEventsService } from 'app/api/services/signal.service';
import { GeneralDataRepository } from 'app/helpers/repository/general-data.repository';
import { NotificationsRepository } from 'app/state/notifications.repository';
import { totalUnreadCount } from 'app/helpers/notifications/page-notification-count';
import { TenantMenuItemData } from 'app/api/models/auth/sideBarInfoDto';
import { combineLatest, fromEvent, map, Observable, startWith } from 'rxjs';
import { TenantBrandingService } from 'app/state/tenant-branding.service';

@Component({
    selector: 'app-headermenu',
    templateUrl: './headermenu.component.html',
    styleUrls: ['./headermenu.component.scss'],
    standalone: false
})
export class HeadermenuComponent implements OnInit {
  showBackArrow$!: Observable<boolean>;
  pageTitle$!: Observable<string>;
  isHomePage$!: Observable<boolean>;
  unreadNotifications$!: Observable<number>;
  tenantLabel = '';
  isPilot = false;
  isNybolig = false;

  // Custom brand logo set on the active tenant's "Information" tab. When
  // present (and the tenant isn't Nybolig) it replaces the default IGNTR mark.
  customLogoUrl$!: Observable<string | null>;

  private readonly NYBOLIG_TENANT_ID = '8fa1d45f-a881-40e5-9477-08dc7d94ec51';

  /** Drives the `*ngIf="data$ | async as data"` wrapper in the template
   *  — gives us synchronous access to activeUserData (image / name / surname)
   *  when the avatar is rendered. */
  data$ = this.generalDataRepo.allGeneralData$;

  constructor(
    public navigationService: BackNavigationService,
    public repo: UiRepository,
    private router: Router,
    private route: ActivatedRoute,
    private uiEvents: UiEventsService,
    private generalDataRepo: GeneralDataRepository,
    private notificationsRepo: NotificationsRepository,
    private branding: TenantBrandingService,
  ) {
    this.route.queryParams.subscribe(() => this.initShowBackArrow());
  }

  ngOnInit(): void {
    this.showBackArrow$ = this.initShowBackArrow();
    this.pageTitle$ = this.initPageTitle();
    this.isHomePage$ = this.initIsHomePage();
    this.unreadNotifications$ = this.initUnreadCount();
    this.customLogoUrl$ = this.branding.homeLogoUrl$;

    // Tenant name comes from generalData.activeUserData.tenantName /
    // generalData.tenant?.name depending on payload shape. Use a defensive
    // chain — show whichever is available, fall back to empty string.
    this.generalDataRepo.allGeneralData$.subscribe((data: any) => {
      this.tenantLabel =
        data?.tenant?.name ??
        data?.activeTenant?.name ??
        data?.activeUserData?.tenantName ??
        '';
      this.isNybolig =
        localStorage.getItem('activeTenantId')?.toLowerCase() ===
        this.NYBOLIG_TENANT_ID;
    });
  }

  backnav(): boolean {
    this.isPilot = this.router.url.includes('pilot');
    if (this.isPilot) {
      this.uiEvents.emitAction();
      return true;
    } else {
      this.navigationService.goBack();
      return false;
    }
  }

  goToNotifications(): void {
    this.router.navigate(['/notifications/received']);
  }

  isRoute(route: string): boolean {
    return this.router.url.includes(route);
  }

  /** Same logic as before: show the back arrow when the current URL contains
   *  a GUID, is a "/new" detail route, or on mobile widths (<=768) AND the
   *  route isn't /home. */
  initShowBackArrow(): Observable<boolean> {
    const guidRegex =
      /[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}/;

    const url$ = this.router.events.pipe(
      startWith(this.router.url),
      map(() => this.router.url),
    );

    const resize$ = fromEvent(window, 'resize').pipe(
      startWith(window.innerWidth),
      map(() => window.innerWidth <= 768),
    );

    return combineLatest([url$, resize$]).pipe(
      map(([currentUrl, isMobile]) => {
        const hasGuid = guidRegex.test(currentUrl);
        return (
          !this.isRoute('/home') &&
          (hasGuid || isMobile || this.isRoute('/new'))
        );
      }),
    );
  }

  /** Derive a short page title from the current URL — first non-empty path
   *  segment, title-cased. Falls back to "IGNTR". Stays in sync with route
   *  changes by listening to NavigationEnd. */
  initPageTitle(): Observable<string> {
    const url$ = this.router.events.pipe(
      startWith(null),
      map(() => this.router.url),
    );
    // Recompute when the tenant data arrives too, so a renamed feature
    // (e.g. Sprints → "Mine forløb") shows the custom title the same way the
    // bottom nav, sidebar and page heading do. startWith(null) keeps the
    // title visible before general data has loaded (falls back to TITLE_MAP).
    const menu$ = this.generalDataRepo.allGeneralData$.pipe(
      startWith(null as any),
      map((data: any) => (data?.tenantMenuItemsData ?? []) as TenantMenuItemData[]),
    );
    return combineLatest([url$, menu$]).pipe(
      map(([url, menuItems]) => this.urlToTitle(url, menuItems)),
    );
  }

  /** True when the current route is the home page — used to hide the
   *  redundant "Home" title in the mobile top bar. */
  private initIsHomePage(): Observable<boolean> {
    return this.router.events.pipe(
      startWith(null),
      map(() => this.router.url),
      map((url) => {
        const first = (url || '')
          .split('?')[0]
          .split('#')[0]
          .split('/')
          .filter(Boolean)[0];
        return !first || first.toLowerCase() === 'home';
      }),
    );
  }

  private urlToTitle(
    url: string,
    menuItems: TenantMenuItemData[] = [],
  ): string {
    const path = (url || '').split('?')[0].split('#')[0];
    const segments = path.split('/').filter(Boolean);
    if (!segments.length) return 'IGNTR';
    const first = segments[0].toLowerCase();
    // The combined onboarding/sprints ADMIN page (/onboarding/admin/*) belongs
    // to the Sprints ("Mine forløb") area, so resolve its title from the
    // Sprints feature rather than the separate Onboarding custom title. The
    // user-facing /onboarding/:id pages keep their own Onboarding title.
    const featureKey =
      first === 'onboarding' && segments[1]?.toLowerCase() === 'admin'
        ? 'sprints'
        : first;
    // Prefer the tenant's custom feature title when one is set — matches the
    // case-insensitive lookup the navbar/sidebar/page-hero use. Only override
    // when a customTitle exists; otherwise keep the friendly TITLE_MAP label.
    const custom = menuItems.find(
      (m) => m.name?.toLocaleLowerCase() === featureKey,
    )?.customTitle;
    if (custom) return custom;
    return this.TITLE_MAP[featureKey] ?? this.titleCase(featureKey);
  }

  private titleCase(s: string): string {
    return s.charAt(0).toUpperCase() + s.slice(1);
  }

  /** Friendly labels for common routes — keeps mobile titles short and
   *  human-readable instead of raw slugs. */
  private readonly TITLE_MAP: Record<string, string> = {
    home: 'Home',
    feed: 'Feed',
    posts: 'News',
    news: 'News',
    sprints: 'Sprints',
    toolbox: 'Tools',
    personalplan: 'Plan',
    personalcoach: 'Coach',
    notifications: 'Notifications',
    profile: 'Profile',
    'data-room': 'Data Room',
    crm: 'CRM',
    chat: 'Chat',
    calendar: 'Calendar',
    timelogs: 'Time',
    todo: 'Todo',
    'ai-roleplay': 'Roleplay',
    'ai-selling': 'AI Selling',
    playbook: 'PlayBook',
    pilot: 'AI Pilot',
    tenants: 'Tenants',
    users: 'Users',
    'user-groups': 'User Groups',
    'out-of-office-status': 'Out of Office',
    onboarding: 'Onboarding',
    kemihotelletapv: 'KemiHotellet',
    product: 'SDS Links',
    whistleblow: 'Whistleblow',
    phonebook: 'Phonebook',
    mailing: 'Messages',
    absent: 'Absent',
    currency: 'Currency',
  };

  /** Whole-app unread total for the bell badge. The mobile header bell is the
   *  notification-center entry (it opens /notifications/received), so it shows
   *  the global total consistently on every page — not a page-specific count
   *  that would appear on some routes and vanish on others. */
  private initUnreadCount(): Observable<number> {
    return this.notificationsRepo.notificationCount$.pipe(
      map((value) => totalUnreadCount(value)),
    );
  }
}
